// File: pages/admin/AdminUserDetails.jsx
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { User, ArrowLeft, Loader2, AlertCircle, Edit3, Save, X, Lock, ShoppingBag, Eye, CheckCircle, Coins, Truck, Tag, Settings, Calendar, User as UserIcon, Mail, Image } from 'lucide-react';
import { authAPI, ordersAPI, productsAPI } from '../../services/api';
import './css/AdminUserDetails.css';

// Safe option label formatter
const formatOptionLabel = (group, value) => {
  const extraPrice = Number(value.extra_price) || 0;
  const extra = extraPrice > 0 ? ` (+DT ${extraPrice.toFixed(2)})` : '';
  return `${group.name}: ${value.value}${extra}`;
};

const AdminUserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [enrichedOrders, setEnrichedOrders] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, ordersRes] = await Promise.all([
          authAPI.getUserById(id),
          ordersAPI.getOrdersByUser(id)
        ]);
        setUser(userRes.data);
        setOrders(ordersRes.data);
        
        // Enrich orders with option prices
        const enriched = await enrichOrdersWithOptions(ordersRes.data);
        setEnrichedOrders(enriched);
        
        setFormData({
          firstName: userRes.data.firstName,
          lastName: userRes.data.lastName,
          phoneNumber: userRes.data.phoneNumber || '',
          shippingAddress: userRes.data.shippingAddress || '',
          email: userRes.data.email,
          isAdmin: userRes.data.isAdmin
        });
      } catch (err) {
        setError(err.response?.data?.message || 'Impossible de charger les donnees');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const enrichOrdersWithOptions = async (ordersList) => {
    const itemsWithOptions = ordersList.flatMap(order => 
      order.items
        .filter(item => {
          let parsedOptions = {};
          try {
            parsedOptions = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
          } catch {
            parsedOptions = {};
          }
          return parsedOptions && Object.keys(parsedOptions).length > 0;
        })
        .map(item => ({ ...item, orderId: order.id }))
    );

    if (itemsWithOptions.length === 0) {
      return ordersList.map(order => ({
        ...order,
        enrichedItems: order.items.map(item => ({
          ...item,
          finalPrice: Number(item.price || 0),
          optionLabels: null
        }))
      }));
    }

    const uniqueProductIds = [...new Set(itemsWithOptions.map(item => item.productId))];
    const productResponses = await Promise.allSettled(
      uniqueProductIds.map(id => productsAPI.getById(id))
    );

    const productsMap = {};
    productResponses.forEach((resp, idx) => {
      if (resp.status === 'fulfilled') {
        productsMap[uniqueProductIds[idx]] = resp.value.data;
      }
    });

    return ordersList.map(order => {
      const enrichedItems = order.items.map(item => {
        let parsedOptions = {};
        try {
          parsedOptions = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
        } catch {
          parsedOptions = {};
        }

        const fullProduct = productsMap[item.productId];
        if (!fullProduct || Object.keys(parsedOptions).length === 0) {
          return {
            ...item,
            finalPrice: Number(item.price || 0),
            optionLabels: null
          };
        }

        const basePrice = Number(fullProduct.salePrice || fullProduct.regularPrice || 0);
        let optionExtra = 0;
        const labels = [];

        fullProduct.options.forEach(group => {
          const selectedValueId = parsedOptions[group.id];
          if (selectedValueId) {
            const value = group.values.find(v => v.id === Number(selectedValueId));
            if (value) {
              const extra = Number(value.extra_price) || 0;
              optionExtra += extra;
              labels.push(formatOptionLabel(group, value));
            }
          }
        });

        const finalPrice = basePrice + optionExtra;

        return {
          ...item,
          finalPrice,
          optionLabels: labels.length > 0 ? labels : null,
          name: fullProduct.name || item.name,
          imageUrl: fullProduct.images?.[0]?.url || item.imageUrl
        };
      });

      return {
        ...order,
        enrichedItems
      };
    });
  };

  const getImageUrl = (imageUrl) => {
    return imageUrl && !imageUrl.startsWith('http') ? `/images/${imageUrl}` : imageUrl || '/placeholder-image.jpg';
  };

  const getStatusLabel = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'En attente';
      case 'processing': return 'En cours';
      case 'shipped': return 'Expedie';
      case 'delivered': return 'Livree';
      default: return status;
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await authAPI.updateUser(id, formData);
      setUser({ ...user, ...formData });
      setEditMode(false);
      setSuccess('Utilisateur mis a jour avec succes');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Echec de mise a jour de l utilisateur');
      setSuccess('');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.password !== passwordData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    try {
      await authAPI.changePassword(id, passwordData);
      setPasswordData({ password: '', confirmPassword: '' });
      setSuccess('Mot de passe modifie avec succes');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Echec de modification du mot de passe');
      setSuccess('');
    }
  };

  const closeAlert = () => {
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div className="admin-user-details-loading-container">
        <div className="admin-user-details-loading-content">
          <Loader2 className="admin-user-details-loading-spinner" />
          <p className="admin-user-details-loading-text">Chargement des details utilisateur...</p>
        </div>
      </div>
    );
  }

  if (error && !success) {
    return (
      <div className="admin-user-details-error">
        <AlertCircle className="admin-user-details-error-icon" />
        <p>{error}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-user-details-not-found">
        <AlertCircle className="admin-user-details-not-found-icon" />
        <h3>Utilisateur introuvable</h3>
        <p>L utilisateur demande est introuvable</p>
      </div>
    );
  }

  return (
    <div className="admin-user-details-container">
      {/* Header */}
      <header className="admin-user-details-header">
        <div className="admin-user-details-header-content">
          <button onClick={() => navigate('/admin/users')} className="admin-user-details-back-btn">
            <ArrowLeft className="admin-user-details-back-icon" />
            <span>Retour aux utilisateurs</span>
          </button>
          <div className="admin-user-details-header-right">
            <div className="admin-user-details-header-icon-wrapper">
              <User className="admin-user-details-header-icon" />
            </div>
            <div className="admin-user-details-header-text">
              <h1 className="admin-user-details-title">
                {user.firstName} {user.lastName}
              </h1>
              <p className="admin-user-details-subtitle">Details utilisateur et historique des commandes</p>
            </div>
          </div>
        </div>
      </header>

      {/* Alerts */}
      {(error || success) && (
        <div className="admin-user-details-alerts">
          {error && (
            <div className="admin-user-details-alert admin-user-details-alert-error">
              <AlertCircle className="admin-user-details-alert-icon" />
              <p className="admin-user-details-alert-text">{error}</p>
              <button className="admin-user-details-alert-close" onClick={closeAlert}>
                <X size={16} />
              </button>
            </div>
          )}
          {success && (
            <div className="admin-user-details-alert admin-user-details-alert-success">
              <CheckCircle className="admin-user-details-alert-icon" />
              <p className="admin-user-details-alert-text">{success}</p>
              <button className="admin-user-details-alert-close" onClick={closeAlert}>
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <main className="admin-user-details-content">
        {/* Profile Information */}
        <section className="admin-user-details-card">
          <div className="admin-user-details-card-header">
            <div className="admin-user-details-card-title-wrapper">
              <User className="admin-user-details-card-icon" />
              <h2 className="admin-user-details-card-title">Informations du profil</h2>
            </div>
            {!editMode && (
              <button onClick={handleEditToggle} className="admin-user-details-edit-btn">
                <Edit3 size={16} />
                <span>Modifier</span>
              </button>
            )}
          </div>
          <div className="admin-user-details-card-body">
            {!editMode ? (
              <div className="admin-user-details-info-grid">
                <div className="admin-user-details-info-item">
                  <label className="admin-user-details-info-label">ID utilisateur</label>
                  <p className="admin-user-details-info-value">#{String(user.id)}</p>
                </div>
                <div className="admin-user-details-info-item">
                  <label className="admin-user-details-info-label">Adresse email</label>
                  <p className="admin-user-details-info-value">{user.email}</p>
                </div>
                <div className="admin-user-details-info-item">
                  <label className="admin-user-details-info-label">Nom complet</label>
                  <p className="admin-user-details-info-value">{user.firstName} {user.lastName}</p>
                </div>
                <div className="admin-user-details-info-item">
                  <label className="admin-user-details-info-label">Telephone</label>
                  <p className="admin-user-details-info-value">{user.phoneNumber || 'N/D'}</p>
                </div>
                <div className="admin-user-details-info-item">
                  <label className="admin-user-details-info-label">Adresse de livraison</label>
                  <p className="admin-user-details-info-value">
                    {user.shippingAddress || 'N/D'}
                  </p>
                </div>
                <div className="admin-user-details-info-item">
                  <label className="admin-user-details-info-label">Role du compte</label>
                  <span className={`admin-user-details-badge ${user.isAdmin ? 'admin-user-details-badge-admin' : 'admin-user-details-badge-user'}`}>
                    {user.isAdmin ? 'Administrateur' : 'Client'}
                  </span>
                </div>
                <div className="admin-user-details-info-item">
                  <label className="admin-user-details-info-label">Membre depuis</label>
                  <p className="admin-user-details-info-value">
                    {new Date(user.createdAt).toLocaleDateString('fr-FR', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdateUser} className="admin-user-details-edit-form">
                <div className="admin-user-details-form-grid">
                  <div className="admin-user-details-form-group">
                    <label className="admin-user-details-form-label">Prenom</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="admin-user-details-input"
                    />
                  </div>
                  <div className="admin-user-details-form-group">
                    <label className="admin-user-details-form-label">Nom</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="admin-user-details-input"
                    />
                  </div>
                  <div className="admin-user-details-form-group">
                    <label className="admin-user-details-form-label">Adresse email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="admin-user-details-input"
                    />
                  </div>
                  <div className="admin-user-details-form-group">
                    <label className="admin-user-details-form-label">Telephone</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="admin-user-details-input"
                    />
                  </div>
                  <div className="admin-user-details-form-group">
                    <label className="admin-user-details-form-label">Adresse de livraison</label>
                    <textarea
                      name="shippingAddress"
                      value={formData.shippingAddress}
                      onChange={handleInputChange}
                      rows={3}
                      className="admin-user-details-textarea"
                    />
                  </div>
                  <div className="admin-user-details-form-group">
                    <label className="admin-user-details-form-label">Role du compte</label>
                    <label className="admin-user-details-checkbox-wrapper">
                      <input
                        type="checkbox"
                        name="isAdmin"
                        checked={formData.isAdmin}
                        onChange={(e) => setFormData({...formData, isAdmin: e.target.checked})}
                        className="admin-user-details-checkbox"
                      />
                      <span className="admin-user-details-checkbox-label">Accorder acces administrateur</span>
                    </label>
                  </div>
                </div>
                <div className="admin-user-details-form-actions">
                  <button type="submit" className="admin-user-details-save-btn">
                    <Save size={16} />
                    <span>Enregistrer</span>
                  </button>
                  <button type="button" onClick={handleEditToggle} className="admin-user-details-cancel-btn">
                    <X size={16} />
                    <span>Annuler</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* Password Change */}
        <section className="admin-user-details-card">
          <div className="admin-user-details-card-header">
            <div className="admin-user-details-card-title-wrapper">
              <Lock className="admin-user-details-card-icon" />
              <h2 className="admin-user-details-card-title">Parametres de securite</h2>
            </div>
          </div>
          <div className="admin-user-details-card-body">
            <form onSubmit={handleChangePassword} className="admin-user-details-password-form">
              <div className="admin-user-details-form-grid">
                <div className="admin-user-details-form-group">
                  <label className="admin-user-details-form-label">Nouveau mot de passe</label>
                  <input
                    type="password"
                    name="password"
                    value={passwordData.password}
                    onChange={handlePasswordChange}
                    placeholder="Saisir un nouveau mot de passe"
                    minLength={6}
                    required
                    className="admin-user-details-input"
                  />
                </div>
                <div className="admin-user-details-form-group">
                  <label className="admin-user-details-form-label">Confirmer le mot de passe</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Confirmer le nouveau mot de passe"
                    minLength={6}
                    required
                    className="admin-user-details-input"
                  />
                </div>
              </div>
              <div className="admin-user-details-form-actions">
                <button type="submit" className="admin-user-details-password-btn">
                  <Lock size={16} />
                  <span>Changer le mot de passe</span>
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* Orders History */}
        <section className="admin-user-details-card">
          <div className="admin-user-details-card-header">
            <div className="admin-user-details-card-title-wrapper">
              <ShoppingBag className="admin-user-details-card-icon" />
              <h2 className="admin-user-details-card-title">Historique des commandes</h2>
            </div>
          </div>
          <div className="admin-user-details-card-body">
            {enrichedOrders.length === 0 ? (
              <div className="admin-user-details-empty">
                <ShoppingBag className="admin-user-details-empty-icon" />
                <h3 className="admin-user-details-empty-title">Aucune commande trouvee</h3>
                <p className="admin-user-details-empty-text">Cet utilisateur n a pas encore passe de commande</p>
              </div>
            ) : (
              <div className="admin-user-details-orders-list">
                {enrichedOrders.map((order) => {
                  const subtotal = order.enrichedItems.reduce((sum, item) => sum + (item.finalPrice * item.quantity), 0);
                  const discount = Number(order.discount_amount || 0);
                  const deliveryFee = Number(order.delivery_fee || 0);
                  const total = Math.max(0, subtotal - discount + deliveryFee);

                  return (
                    <div key={order.id} className="admin-user-details-order-card">
                      <div className="admin-user-details-order-header">
                        <div className="admin-user-details-order-info">
                          <span className="admin-user-details-order-id">#{String(order.id)}</span>
                          <div className="admin-user-details-order-date">
                            <Calendar size={12} />
                            <span>{new Date(order.createdAt).toLocaleDateString('fr-FR', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}</span>
                          </div>
                        </div>
                        <div className={`admin-user-details-status-badge admin-user-details-status-${order.status}`}>
                          {getStatusLabel(order.status)}
                        </div>
                      </div>

                      <div className="admin-user-details-order-items">
                        {order.enrichedItems.slice(0, 4).map((item, idx) => (
                          <div key={idx} className="admin-user-details-order-item">
                            <img 
                              src={getImageUrl(item.imageUrl)} 
                              alt={item.name || 'Produit'}
                              className="admin-user-details-order-item-image"
                              onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                            />
                            <div className="admin-user-details-order-item-details">
                              <span className="admin-user-details-order-item-name">
                                {item.name}
                              </span>
                              <div className="admin-user-details-order-item-meta">
                                <span className="admin-user-details-order-item-qty">Qte : {item.quantity}</span>
                                {item.optionLabels && (
                                  <div className="admin-user-details-order-item-options">
                                    <Settings size={10} />
                                    <span>{item.optionLabels[0]}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="admin-user-details-order-item-price">
                              DT {(item.finalPrice * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        ))}
                        {order.enrichedItems.length > 4 && (
                          <div className="admin-user-details-order-more-items">
                            +{order.enrichedItems.length - 4} article{order.enrichedItems.length - 4 > 1 ? 's' : ''} en plus
                          </div>
                        )}
                      </div>

                      <div className="admin-user-details-order-summary">
                          <div className="admin-user-details-order-summary-row">
                            <span>Sous-total</span>
                            <span>DT {subtotal.toFixed(2)}</span>
                          </div>
                          {discount > 0 && (
                            <div className="admin-user-details-order-summary-row admin-user-details-order-discount">
                              <span>Remise</span>
                              <span>-DT {discount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="admin-user-details-order-summary-row">
                            <span>Livraison</span>
                            <span>{deliveryFee === 0 ? 'Gratuite' : `DT ${deliveryFee.toFixed(2)}`}</span>
                          </div>
                        <div className="admin-user-details-order-summary-divider"></div>
                          <div className="admin-user-details-order-summary-total">
                            <span>Total</span>
                            <span className="admin-user-details-order-total-amount">
                              <Coins size={16} />
                              DT {total.toFixed(2)}
                            </span>
                          </div>
                      </div>
                      <div className="admin-user-details-order-actions">
                        <Link to={`/admin/orders/${order.id}`} className="admin-user-details-action-btn">
                          <Eye size={14} />
                          <span>Voir details</span>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminUserDetails;
